import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Input, HStack, Button, Text } from "@wootingkb/component-library";
import { useState, FormEvent, MouseEventHandler, useCallback } from "react";
import {
  PHONENUMBER_LABEL,
  OTP_LABEL,
} from "../../../utils/constants/i18n-strings";
import { useSignin } from "../../../utils/hooks/useSignIn";
import { LoginMethodProps } from "./types";
import { PhoneNumberInput, PhoneValue } from "../Helpers/PhoneNumberInput";
import {
  RegionalInstanceCapability,
  useRegionalInstanceCapability,
} from "../../../lib/[services]/regional-instance";

const OTP_LOGIN_BUTTON = msg({
  id: "Auth_otp_login_button",
  message: "Login/Register",
});
const SEND_OTP_BUTTON = msg({
  id: "Auth_send_otp_button",
  message: "Send Code",
});

export default function PhoneOTPLogin({ setLoading }: LoginMethodProps) {
  const [phoneNumber, setPhoneNumber] = useState("");
  // National part only (no dial code) — drives the input's displayed value so
  // the country selector, not the text box, owns the prefix.
  const [nationalNumber, setNationalNumber] = useState("");
  const [phoneNumberValid, setPhoneNumberValid] = useState(false);
  const [otpCode, setOTPCode] = useState("");
  const [otpDisabled, setOtpDisabled] = useState(false);
  const { signInOTP, sendOTP } = useSignin(true);
  const { _: i18n } = useLingui();
  const showPhoneOTP = useRegionalInstanceCapability(
    RegionalInstanceCapability.PhoneOTP
  );

  const autoFocusFn = useCallback(
    (element: HTMLInputElement | null) => {
      if (element && phoneNumber.length === 0) {
        element.focus();
      }
    },
    [phoneNumber]
  );

  async function requestOtp() {
    setOtpDisabled(true);
    const success = await sendOTP(phoneNumber);
    if (!success) setOtpDisabled(false);
    return success;
  }

  async function otpSignInFormHandler(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // Before a code has been requested, pressing enter sends the code first
    // rather than attempting to log in.
    if (!otpDisabled) {
      if (phoneNumberValid) await requestOtp();
      return;
    }
    setLoading(true);
    const success = await signInOTP(phoneNumber, otpCode);
    if (!success) setLoading(false);
  }

  async function requestOtpHandler(
    e: Parameters<MouseEventHandler<HTMLButtonElement>>[0]
  ) {
    e.preventDefault();
    await requestOtp();
  }

  async function phoneNumberChangeHandler(phone: PhoneValue) {
    setPhoneNumber(phone.e164);
    setNationalNumber(phone.nationalNumber);

    setPhoneNumberValid(/^\+861[3-9](\d{9}|\d{11})$/.test(phone.e164));
  }

  if (!showPhoneOTP) {
    return <></>;
  }

  return (
    <form onSubmit={otpSignInFormHandler} className="flex flex-col gap-4">
      <PhoneNumberInput
        id="phonenumber-input"
        placeholder={i18n(PHONENUMBER_LABEL)}
        autoComplete="tel"
        onChange={phoneNumberChangeHandler}
        value={nationalNumber}
        isInvalid={nationalNumber.length > 0 && !phoneNumberValid}
        ref={autoFocusFn}
        className="rb-mask"
        // renderFlag={(c) => {
        //   const F = Flags[c.iso2 as keyof typeof Flags];
        //   return <F className="h-3.5 w-5 rounded-[1px]" />;
        // }}
      />
      <HStack className="gap-4">
        <Input
          id="otp-input"
          type="text"
          inputMode="numeric"
          placeholder={i18n(OTP_LABEL)}
          autoComplete="one-time-code"
          onChange={(e) => setOTPCode(e.target.value)}
          isDisabled={!otpDisabled}
          autoFocus={phoneNumber.length > 0}
        />

        <Button
          type="button"
          variant="secondary"
          // Component library input elements automatilly get set to h-12 by default
          // so align button with it
          className="h-12 min-h-12"
          onClick={requestOtpHandler}
          isDisabled={
            otpDisabled || (phoneNumber.length > 0 && !phoneNumberValid)
          }
        >
          {i18n(SEND_OTP_BUTTON)}
        </Button>
      </HStack>
      <HStack className="w-full items-center justify-between">
        <Text className="text-sm text-foreground-secondary">&nbsp;</Text>
      </HStack>
      <Button type="submit" size="full" variant="secondary" className="mt-4">
        {i18n(OTP_LOGIN_BUTTON)}
      </Button>
    </form>
  );
}
